<script setup>
import { ref, computed, watch } from 'vue';
import Chart from "primevue/chart";

const props = defineProps({
  currentAssetAllocation: {
    type: Array,
    default: () => []
  },
  targetAssetAllocation: {
    type: Array,
    default: () => []
  }
});

const chartData = ref({});
const chartOptions = ref({});

// Get all asset allocation data including missing assets for chart display
const getAllAssetAllocationData = computed(() => {
  const current = props.currentAssetAllocation;
  const target = props.targetAssetAllocation;

  // Create maps for current and target allocations
  const currentMap = {};
  current.forEach(asset => {
    currentMap[asset.AssetClassName] = asset;
  });

  const targetMap = {};
  target.forEach(asset => {
    targetMap[asset.AssetClassName] = asset;
  });

  // Get all unique asset class names
  const allAssetNames = new Set([
    ...current.map(asset => asset.AssetClassName),
    ...target.map(asset => asset.AssetClassName)
  ]);

  // Create combined data for all assets
  const allAssets = Array.from(allAssetNames).map(assetName => {
    const currentAsset = currentMap[assetName];
    const targetAsset = targetMap[assetName];

    const currentPct = currentAsset ? currentAsset.Current_Pct : 0;
    const targetPct = targetAsset ? targetAsset.TargetPct : 0;
    const marketValue = currentAsset ? currentAsset.Market_Value : 0;
    const deviation = currentPct - targetPct;

    return {
      AssetClassName: assetName,
      Current_Pct: currentPct,
      Target_Pct: targetPct,
      Market_Value: marketValue,
      Deviation: deviation,
      IsMissing: !currentAsset // Flag to identify missing assets
    };
  });

  // Sort: existing assets by market value descending, then missing assets by target percentage descending
  return allAssets.sort((a, b) => {
    if (a.IsMissing && !b.IsMissing) return 1; // Missing assets go to the end
    if (!a.IsMissing && b.IsMissing) return -1; // Existing assets come first
    if (a.IsMissing && b.IsMissing) return b.Target_Pct - a.Target_Pct; // Sort missing by target %
    return b.Market_Value - a.Market_Value; // Sort existing by market value
  });
});

// Format chart data
const formatChartData = () => {
  const allAssets = getAllAssetAllocationData.value;

  if (allAssets.length === 0) return;

  // Filter to only show existing assets in the chart (not missing ones)
  const existingAssets = allAssets.filter(asset => !asset.IsMissing);

  const labels = existingAssets.map(item => item.AssetClassName);
  const currentData = existingAssets.map(item => item.Current_Pct);
  const targetData = existingAssets.map(item => item.Target_Pct);

  chartData.value = {
    labels: labels,
    datasets: [
      {
        label: 'Current %',
        data: currentData,
        backgroundColor: 'rgba(54, 162, 235, 0.7)',
        borderColor: 'rgb(54, 162, 235)',
        borderWidth: 2,
        borderRadius: 4,
        borderSkipped: false,
      },
      {
        label: 'Target %',
        data: targetData,
        backgroundColor: 'rgba(255, 99, 132, 0.7)',
        borderColor: 'rgb(255, 99, 132)',
        borderWidth: 2,
        borderRadius: 4,
        borderSkipped: false,
      }
    ]
  };

  chartOptions.value = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
        labels: {
          usePointStyle: true,
          padding: 20,
          font: {
            size: 12,
            weight: 600
          }
        }
      },
      tooltip: {
        mode: 'index',
        intersect: false,
        callbacks: {
          label: function(context) {
            const label = context.dataset.label || '';
            const value = context.parsed.y;
            const assetIndex = context.dataIndex;
            const existingAssets = getAllAssetAllocationData.value.filter(asset => !asset.IsMissing);
            const asset = existingAssets[assetIndex];
            const deviation = asset ? asset.Deviation : 0;

            if (label === 'Current %') {
              return `${label}: ${value.toFixed(1)}% (Deviation: ${deviation > 0 ? '+' : ''}${deviation.toFixed(1)}%)`;
            }
            return `${label}: ${value.toFixed(1)}%`;
          }
        }
      }
    },
    scales: {
      x: {
        grid: {
          display: false
        },
        ticks: {
          font: {
            size: 11,
            weight: 600
          },
          maxRotation: 45,
          minRotation: 0
        }
      },
      y: {
        beginAtZero: true,
        grid: {
          color: '#f0f0f0'
        },
        ticks: {
          font: {
            size: 11
          },
          callback: function(value) {
            return value + '%';
          }
        },
        title: {
          display: true,
          text: 'Allocation Percentage (%)',
          font: {
            size: 12,
            weight: 600
          }
        }
      }
    },
    interaction: {
      mode: 'nearest',
      axis: 'x',
      intersect: false
    }
  };
};

// Format percentage helper
const formatPercentage = (value) => {
  return `${value?.toFixed(1) || 0}%`;
};

// Get deviation status class for styling
const getDeviationClass = (deviation) => {
  if (Math.abs(deviation) <= 2) return 'text-success';
  if (deviation > 2) return 'text-warning';
  return 'text-error';
};

// Watch for data changes
watch([() => props.currentAssetAllocation, () => props.targetAssetAllocation], () => {
  formatChartData();
}, { immediate: true, deep: true });
</script>

<template>
  <div class="asset-allocation-chart-container">
    <!-- Chart Section -->
    <div class="chart-section">
      <div class="chart-wrapper">
        <Chart
          type="bar"
          :data="chartData"
          :options="chartOptions"
          class="allocation-chart"
        />
      </div>
    </div>

    <!-- Deviation Summary -->
    <div class="deviation-summary mt-20">
      <h4 class="summary-title">Allocation Deviations (All Assets)</h4>
      <div class="deviation-grid">
        <div
          v-for="asset in getAllAssetAllocationData"
          :key="asset.AssetClassName"
          :class="['deviation-item', { 'missing-asset-item': asset.IsMissing }]"
        >
          <div class="asset-info">
            <div class="asset-name-container">
              <span class="asset-name">{{ asset.AssetClassName }}</span>
              <span v-if="asset.IsMissing" class="missing-badge">Missing</span>
            </div>
            <div class="allocation-values">
              <span class="current-value">Current: {{ formatPercentage(asset.Current_Pct) }}</span>
              <span class="target-value">Target: {{ formatPercentage(asset.Target_Pct) }}</span>
            </div>
          </div>
          <div class="deviation-value">
            <span :class="[getDeviationClass(asset.Deviation), 'deviation-text']">
              {{ asset.Deviation > 0 ? '+' : '' }}{{ formatPercentage(asset.Deviation) }}
            </span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.asset-allocation-chart-container {
  width: 100%;
  height: 100%;
  padding: 10px 0;
}

.chart-section {
  width: 100%;
  margin-bottom: 20px;
}

.chart-wrapper {
  width: 100%;
  height: 350px;
  padding: 10px;
  background: #fafafa;
  border-radius: 8px;
  border: 1px solid #e9ecef;
}

.allocation-chart {
  width: 100% !important;
  height: 100% !important;
}

.deviation-summary {
  border-top: 1px solid #e9ecef;
  padding-top: 15px;
}

.summary-title {
  font-size: 1rem;
  font-weight: 700;
  color: var(--an-text-primary);
  margin-bottom: 12px;
  margin-top: 0;
}

.deviation-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 10px;
}

.deviation-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 12px;
  background: #f8f9fa;
  border-radius: 6px;
  border: 1px solid #e9ecef;
}

.asset-info {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.asset-name {
  font-size: 13px;
  font-weight: 600;
  color: var(--an-text-primary);
}

.allocation-values {
  display: flex;
  gap: 12px;
}

.current-value,
.target-value {
  font-size: 11px;
  color: var(--an-text-muted);
  font-weight: 600;
}

.current-value {
  color: rgb(54, 162, 235);
}

.target-value {
  color: rgb(255, 99, 132);
}

.deviation-value {
  display: flex;
  align-items: center;
}

.deviation-text {
  font-size: 13px;
  font-weight: 700;
}

/* Missing Asset Styling */
.missing-asset-item {
  background: linear-gradient(135deg, #fff8f8 0%, #fef3f3 100%);
  border: 1px solid #f1aeb5;
  border-left: 3px solid #dc3545;
}

.asset-name-container {
  display: flex;
  align-items: center;
  gap: 8px;
}

.missing-badge {
  background: #dc3545;
  color: white;
  font-size: 10px;
  font-weight: 600;
  padding: 2px 6px;
  border-radius: 8px;
  text-transform: uppercase;
}

/* Color classes for deviation */
.text-success {
  color: #28a745;
}

.text-warning {
  color: #ffc107;
}

.text-error {
  color: #dc3545;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .chart-wrapper {
    height: 300px;
  }

  .deviation-grid {
    grid-template-columns: 1fr;
  }

  .deviation-item {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }

  .allocation-values {
    flex-direction: column;
    gap: 2px;
  }
}
</style>
